import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCampaignById, clearSelectedCampaign } from "../../../Redux/campaignSlice";
import { FaArrowLeft, FaUser, FaMoneyBillWave, FaYoutube, FaInstagram, FaMoneyBill } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCopy, FaList, FaPlus } from "react-icons/fa";
import AgreementModal from "./AgreementModal";
import { campaignService } from "../../services/companyAuthService";


const ViewCampaign = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedCampaign, loading, error } = useSelector((state) => state.campaign);
  
  // Agreement modal state
  const [showEditPayoutModal, setShowEditPayoutModal] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [approvalData, setApprovalData] = useState({
    commissionModel: 'fixed',
    amount: '',
    percentage: '',
    cpe: '',
    cpm: '',
    agreementStatus: 'agreed_with_influencer',
    currency: null
  });
  const [loadingApprovalMeta, setLoadingApprovalMeta] = useState(false);
  const [updatingAgreement, setUpdatingAgreement] = useState(false);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied to clipboard!"); // optional
  };

  const handleViewStats = (campaignId) => {
    navigate(`/company/dashboard/campaigns/${campaignId}/stats`);
  };

  const handleViewPayment = (campaignId, affiliateId) => {
    navigate(`/company/dashboard/campaigns/${campaignId}/affiliate/${affiliateId}/payment`);
  };

  const handleEditPayout = async (affiliate) => {
    try {
      setSelectedAffiliate(affiliate);
      setShowEditPayoutModal(true);
      setLoadingApprovalMeta(true);

      // Try to fetch existing agreement
      try {
        const agreementResponse = await campaignService.getAffiliateAgreement(id, affiliate._id);
        
        if (agreementResponse.success && agreementResponse.data?.agreement) {
          const agreement = agreementResponse.data.agreement;
          setApprovalData({
            commissionModel: agreement.commission?.model || 'fixed',
            amount: agreement.commission?.amount ?? '',
            percentage: agreement.commission?.percentage ?? '',
            cpe: agreement.commission?.cpe ?? '',
            cpm: agreement.commission?.cpm ?? '',
            agreementStatus: agreement.agreementStatus || 'agreed_with_influencer',
            currency: agreement.commission?.currency || null
          });
        } else {
          // Fallback to campaign commission if no agreement found
          loadCampaignCommissionData();
        }
      } catch (agreementError) {
        console.warn('Agreement not found, using campaign defaults:', agreementError);
        // Fallback to campaign commission data
        loadCampaignCommissionData();
      }
    } catch (err) {
      console.error('Error loading payout data:', err);
      toast.error(err.message || 'Failed to load payout details');
      loadCampaignCommissionData();
    } finally {
      setLoadingApprovalMeta(false);
    }
  };

  const loadCampaignCommissionData = () => {
    if (selectedCampaign?.commission) {
      setApprovalData({
        commissionModel: selectedCampaign.commission.model || 'fixed',
        amount: selectedCampaign.commission.amount ?? '',
        percentage: selectedCampaign.commission.percentage ?? '',
        cpe: selectedCampaign.commission.cpe ?? '',
        cpm: selectedCampaign.commission.cpm ?? '',
        agreementStatus: 'agreed_with_influencer',
        currency: selectedCampaign.commission.currency || null
      });
    }
  };

  const handlePayoutUpdate = async () => {
    if (!selectedAffiliate) return;

    const { commissionModel, amount, percentage, cpe, cpm, agreementStatus } = approvalData;

    // Validation
    if (!commissionModel) {
      toast.error('Please select a commission model');
      return;
    }

    if (commissionModel === 'fixed') {
      if (amount === '' || amount === null || amount === undefined) {
        toast.error('Please enter a fixed amount');
        return;
      }
      const numAmount = Number(amount);
      if (isNaN(numAmount) || numAmount < 0) {
        toast.error('Please enter a valid fixed amount (0 or greater)');
        return;
      }
    }

    if (commissionModel === 'percentage') {
      if (percentage === '' || percentage === null || percentage === undefined) {
        toast.error('Please enter a percentage value');
        return;
      }
      const numPercentage = Number(percentage);
      if (isNaN(numPercentage) || numPercentage < 0 || numPercentage > 100) {
        toast.error('Please enter a valid percentage value (0-100)');
        return;
      }
    }

    if (cpe === '' || cpe === null || cpe === undefined) {
      toast.error('Please enter a CPE value');
      return;
    }
    const numCpe = Number(cpe);
    if (isNaN(numCpe) || numCpe < 0) {
      toast.error('Please enter a valid CPE value (0 or greater)');
      return;
    }

    if (cpm === '' || cpm === null || cpm === undefined) {
      toast.error('Please enter a CPM value');
      return;
    }
    const numCpm = Number(cpm);
    if (isNaN(numCpm) || numCpm < 0) {
      toast.error('Please enter a valid CPM value (0 or greater)');
      return;
    }

    if (!agreementStatus) {
      toast.error('Please select an agreement status');
      return;
    }

    try {
      setUpdatingAgreement(true);

      const payload = {
        commissionModel,
        amount: commissionModel === 'fixed' ? Number(amount) : undefined,
        percentage: commissionModel === 'percentage' ? Number(percentage) : undefined,
        cpe: Number(cpe),
        cpm: Number(cpm),
        agreementStatus
      };

      const response = await campaignService.updateAffiliateAgreement(id, selectedAffiliate._id, payload);

      if (response.success) {
        toast.success('Payout details updated successfully');
        setShowEditPayoutModal(false);
        setSelectedAffiliate(null);
        
        // Refresh campaign data to reflect changes
        dispatch(getCampaignById(id));
        
        // Reset form
        setApprovalData({
          commissionModel: 'fixed',
          amount: '',
          percentage: '',
          cpe: '',
          cpm: '',
          agreementStatus: 'agreed_with_influencer',
          currency: approvalData.currency
        });
      } else {
        toast.error(response.message || 'Failed to update payout details');
      }
    } catch (err) {
      console.error('Error updating payout details:', err);
      toast.error(err.message || 'Failed to update payout details');
    } finally {
      setUpdatingAgreement(false);
    }
  };

  useEffect(() => {
    dispatch(getCampaignById(id));

    return () => {
      dispatch(clearSelectedCampaign());
    };
  }, [dispatch, id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-0 md:px-4 py-2 md:py-4">
        <div className="bg-red-100 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!selectedCampaign) {
    return (
      <div className="container mx-auto px-0 md:px-4 py-2 md:py-4">
        <div className="text-center">
          <p className="text-gray-600">Campaign not found</p>
        </div>
      </div>
    );
  }

  // const getStatusColor = (status) => {
  //   switch (status) {
  //     case 'approved':
  //       return 'bg-green-100 text-green-800';
  //     case 'rejected':
  //       return 'bg-red-100 text-red-800';
  //     case 'pending':
  //     default:
  //       return 'bg-yellow-100 text-yellow-800';
  //   }
  // };

  return (
    <div className="container mx-auto px-0 md:px-4 py-2 md:py-4">
      <ToastContainer position="top-right" autoClose={2000} />
      <div className="mb-6">
        <button
          onClick={() => navigate("/company/dashboard/campaigns/")}
          className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200"
        >
          <FaArrowLeft className="mr-2" /> Back
        </button>
      </div>
      <div className="bg-white rounded-lg shadow p-2 md:p-4 mb-10 lg:mb-0">
        <div className="flex flex-row items-center justify-between gap-2 mb-6">
          <h1 className="text-base md:text-xl font-bold text-gray-900 mb-0 whitespace-nowrap">
            Campaign Details
          </h1>

          <Link
            to={`/company/dashboard/affiliates/create`}
            className="bg-orange-500 text-white px-2.5 md:px-4 py-1.5 rounded-lg inline-flex items-center justify-center gap-1 hover:bg-orange-600 transition-colors duration-200 whitespace-nowrap flex-shrink-0 leading-none"
          >
            <FaPlus className="text-xs md:text-sm" />
            <span className="text-xs md:text-sm font-medium">Invite Affiliates</span>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Name</h3>
              <p className="mt-1">{selectedCampaign.name}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Campaign Type</h3>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 rounded-full text-sm ${
                    selectedCampaign.campaignType === "Affiliate"
                      ? "bg-blue-100 text-blue-800"
                      : selectedCampaign.campaignType === "Advertising"
                      ? "bg-purple-100 text-purple-800"
                      : selectedCampaign.campaignType === "Hybrid"
                      ? "bg-orange-100 text-orange-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {selectedCampaign.campaignType}
                </span>
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Description</h3>
              <p className="mt-1">{selectedCampaign.description || 'No description provided'}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Brand</h3>
              <p className="mt-1">{selectedCampaign.brandId?.brandName}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Status</h3>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 rounded-full text-sm ${selectedCampaign.status === "active"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                    }`}
                >
                  {selectedCampaign.status}
                </span>
                {selectedCampaign.isDeactivated && (
                  <span className="ml-2 text-red-600 text-sm">(Permanently Deactivated)</span>
                )}
              </p>
            </div>

            {/* Start Date - Positioned below Status */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-sm font-medium text-blue-700 mb-2 flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Campaign Start Date
              </h3>
              <p className="text-sm text-blue-900 font-semibold">
                {selectedCampaign.startDate
                  ? new Date(selectedCampaign.startDate).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                    : "Not set"}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Commission Model</h3>
              <p className="mt-1">
                {selectedCampaign.commission.model === "fixed" ? "Fixed" : "Percentage"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Payout Amount</h3>
              <p className="mt-1">
                {selectedCampaign.commission.model === "fixed"
                  ? `${selectedCampaign.commission.currency.symbol}${selectedCampaign.commission.amount}`
                  : `${selectedCampaign.commission.percentage}%`}
              </p>
            </div>

            {/* CPE and CPM for Advertising and Hybrid Campaigns */}
            {((selectedCampaign.campaignType === "Advertising" || selectedCampaign.campaignType === "Hybrid") && 
              (selectedCampaign.commission.model === "fixed" || 
               (selectedCampaign.campaignType === "Hybrid" && selectedCampaign.commission.model === "percentage"))) && (
              <>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">CPE (Cost Per Engagement)</h3>
                  <p className="mt-1">
                    {selectedCampaign.commission.currency.symbol}{selectedCampaign.commission.cpe}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">CPM (Cost Per Thousand Views)</h3>
                  <p className="mt-1">
                    {selectedCampaign.commission.currency.symbol}{selectedCampaign.commission.cpm}
                  </p>
                </div>
              </>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-500">Landing Page</h3>
              <div className="flex items-center mt-1 space-x-2">
                <a
                  href={selectedCampaign.landingPage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all max-w-[80%] truncate"
                  title={selectedCampaign.landingPage}
                >
                  {selectedCampaign.landingPage.length > 20
                    ? selectedCampaign.landingPage.slice(0, 20) + "..."
                    : selectedCampaign.landingPage}
                </a>
                <button
                  onClick={() => handleCopy(selectedCampaign.landingPage)}
                  className="text-gray-500 hover:text-gray-700"
                  title="Copy URL"
                >
                  <FaCopy />
                </button>
              </div>
            </div>

            {/* End Date - Positioned below Landing Page */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
              <h3 className="text-sm font-medium text-orange-700 mb-2 flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                Campaign End Date
              </h3>
              <p className="text-sm text-orange-900 font-semibold">
                {selectedCampaign.endDate
                  ? new Date(selectedCampaign.endDate).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                    : "Not set"}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Affiliates</h3>
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
              Total: {selectedCampaign.affiliates?.length || 0}
            </span>
          </div>

          {selectedCampaign.affiliates && selectedCampaign.affiliates.length > 0 ? (
            <div className="overflow-x-auto bg-white rounded-lg border">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product Link
                    </th>
                    {(selectedCampaign.campaignType === "Advertising" || selectedCampaign.campaignType === "Hybrid") && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          YouTube Media
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Instagram Media
                        </th>
                      </>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payout Details
                    </th>


                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedCampaign.affiliates.map((affiliate, index) => (
                    <tr key={affiliate._id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <FaUser className="text-gray-500" />
                            </div>
                          </div>
                          <div className="ml-4">
                            {affiliate.firstName && affiliate.lastName
                              ? `${affiliate.firstName} ${affiliate.lastName}`
                              : affiliate.username || 'Unnamed Affiliate'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {affiliate.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap capitalize">
                        {affiliate.affiliateType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 flex items-center gap-1"
                          title="View Stats"
                          onClick={() => handleViewStats(selectedCampaign._id)}
                        >
                          <FaList />
                          <span className="text-xs">View Stats</span>
                        </button>
                      </td>

                      {(selectedCampaign.campaignType === "Advertising" || selectedCampaign.campaignType === "Hybrid") && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => navigate(`/company/dashboard/youtube-media-approval?campaignId=${selectedCampaign._id}&affiliateId=${affiliate._id}`)}
                              className="flex items-center text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="View YouTube Media"
                            >
                              <FaYoutube className="mr-1" />
                              <span className="text-xs">YouTube Media</span>
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => navigate(`/company/dashboard/instagram-media-approval?campaignId=${selectedCampaign._id}&affiliateId=${affiliate._id}`)}
                              className="flex items-center text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="View Instagram Media"
                            >
                              <FaInstagram className="mr-1" />
                              <span className="text-xs">Instagram Media</span>
                            </button>
                          </td>
                        </>
                      )}

                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewPayment(selectedCampaign._id, affiliate._id)}
                          className="flex items-center text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="View Payment Details"
                        >
                          <FaMoneyBillWave className="mr-1" />
                          <span className="text-xs">Payment Details</span>
                        </button>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleEditPayout(affiliate)}
                          className="flex items-center text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="Edit Payout Details"
                        >
                          <FaMoneyBill className="mr-1" />
                          <span className="text-xs">Payout Details</span>
                        </button>
                      </td>

                      

                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const campaignStatus = affiliate.status || "pending"; // now directly available
                          const statusColor =
                            campaignStatus === "accepted"
                              ? "bg-green-100 text-green-800"
                              : campaignStatus === "rejected"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800";

                          return (
                            <span className={`px-2 py-1 rounded-full text-xs ${statusColor}`}>
                              {campaignStatus}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <FaUser className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Affiliates</h3>
              <p className="mt-1 text-sm text-gray-500">
                This campaign doesn't have any affiliates yet.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Payout Modal */}
      <AgreementModal
        isOpen={showEditPayoutModal}
        onClose={() => {
          setShowEditPayoutModal(false);
          setSelectedAffiliate(null);
          setApprovalData({
            commissionModel: 'fixed',
            amount: '',
            percentage: '',
            cpe: '',
            cpm: '',
            agreementStatus: 'agreed_with_influencer',
            currency: null
          });
        }}
        application={selectedAffiliate ? {
          _id: selectedAffiliate._id,
          name: selectedAffiliate.firstName && selectedAffiliate.lastName
            ? `${selectedAffiliate.firstName} ${selectedAffiliate.lastName}`
            : selectedAffiliate.username || selectedAffiliate.email || 'Affiliate'
        } : null}
        approvalData={approvalData}
        setApprovalData={setApprovalData}
        loadingApprovalMeta={loadingApprovalMeta}
        updatingStatus={{ [selectedAffiliate?._id]: updatingAgreement }}
        onSubmit={handlePayoutUpdate}
        mode="edit"
      />
    </div>
  );
};

export default ViewCampaign;
